import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { hasPermission, PERMISSIONS, isAdmin } from '../../utils/permissions';
import { formatUserName, getEmergencyAdminWarning } from '../../utils/userDisplayUtils';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Backup,
  Person,
  Security,
  Notifications,
  History,
  Warning,
  DeleteForever,
  Group,
  EmojiEvents
} from '@mui/icons-material';
import ResetUtils from '../../utils/resetUtils';

export default function Settings() {
  const { userProfile, currentUser } = useAuth();
  const navigate = useNavigate();
  const [collectionCounts, setCollectionCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [resetDialog, setResetDialog] = useState({ open: false, type: null });
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  // Check if user has admin permissions
  const isUserAdmin = isAdmin(userProfile?.role);
  const canAccessMasterReset = hasPermission(userProfile?.role, PERMISSIONS.MASTER_RESET);
  const canAccessTransactionLogs = hasPermission(userProfile?.role, PERMISSIONS.VIEW_TRANSACTION_LOGS);
  const canManageBackups = hasPermission(userProfile?.role, PERMISSIONS.MANAGE_BACKUPS);

  useEffect(() => {
    loadCollectionCounts();
  }, []);

  const loadCollectionCounts = async () => {
    try {
      setLoading(true);
      const counts = await ResetUtils.getCollectionCounts();
      setCollectionCounts(counts);
    } catch (error) {
      console.error('Error loading collection counts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetClick = (type) => {
    setResetDialog({ open: true, type });
    setResetResult(null);
  };

  const handleResetConfirm = async () => {
    try {
      setResetLoading(true);
      let result;
      
      switch (resetDialog.type) {
        case 'master':
          result = await ResetUtils.masterReset(currentUser.uid, userProfile?.email);
          break;
        case 'players':
          result = await ResetUtils.playerReset(currentUser.uid, userProfile?.email);
          break;
        case 'tournaments':
          result = await ResetUtils.tournamentReset(currentUser.uid, userProfile?.email);
          break;
        default:
          throw new Error('Invalid reset type');
      }
      
      setResetResult(result);
      await loadCollectionCounts(); // Refresh counts
    } catch (error) {
      console.error('Reset error:', error);
      setResetResult({ success: false, error: error.message });
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetClose = () => {
    setResetDialog({ open: false, type: null });
    setResetResult(null);
  };

  const allSettingsOptions = [
    {
      title: 'Backups and Restore',
      description: 'Manage database backups and restore points',
      icon: <Backup />,
      path: '/settings/backups-restore',
      color: 'primary',
      permission: PERMISSIONS.MANAGE_BACKUPS
    },
    {
      title: 'Transaction Log',
      description: 'View and manage system transaction logs',
      icon: <History />,
      path: '/settings/transaction-log',
      color: 'secondary',
      permission: PERMISSIONS.VIEW_TRANSACTION_LOGS
    },
    {
      title: 'User Profile',
      description: 'Manage your personal information and preferences',
      icon: <Person />,
      path: '/settings/profile',
      color: 'info',
      permission: null // Always available
    },
    {
      title: 'Security',
      description: 'Password, authentication, and security settings',
      icon: <Security />,
      path: '/settings/security',
      color: 'warning',
      permission: null // Always available
    },
    {
      title: 'Notifications',
      description: 'Email and system notification preferences',
      icon: <Notifications />,
      path: '/settings/notifications',
      color: 'success',
      permission: null // Always available
    }
  ];

  // Filter settings options based on user permissions
  const settingsOptions = allSettingsOptions.filter(option => 
    !option.permission || hasPermission(userProfile?.role, option.permission)
  );

  const resetOptions = [
    {
      title: 'Master Reset',
      description: 'Deletes everything (Tournaments, Players, Golf Courses, etc.) - Preserves Transaction Logs and Backups',
      icon: <DeleteForever />,
      type: 'master',
      color: 'error',
      warning: 'This will permanently delete ALL data except transaction logs and backups!'
    },
    {
      title: 'Player Reset',
      description: 'Deletes all players and related data',
      icon: <Group />,
      type: 'players',
      color: 'warning',
      count: collectionCounts.players || 0
    },
    {
      title: 'Tournament Reset',
      description: 'Deletes all tournaments and related data',
      icon: <EmojiEvents />,
      type: 'tournaments',
      color: 'warning',
      count: collectionCounts.tournaments || 0
    }
  ];

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {getEmergencyAdminWarning(userProfile)}
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your application settings and preferences
      </Typography>

      <Grid container spacing={3}>
        {settingsOptions.map((option, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: `${option.color}.light`,
                      color: `${option.color}.contrastText`,
                      mr: 2
                    }}
                  >
                    {option.icon}
                  </Box>
                  <Typography variant="h6" component="h2">
                    {option.title}
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary">
                  {option.description}
                </Typography>
              </CardContent>
              
              <CardActions>
                <Button
                  variant="outlined"
                  onClick={() => handleNavigate(option.path)}
                  sx={{ ml: 1 }}
                >
                  Open
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Master Reset Section - Admin Only */}
      {canAccessMasterReset && (
        <>
          <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
            <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
            Master Reset Options
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> These operations will permanently delete data and cannot be undone. 
              Make sure to create a backup before proceeding with any reset operation.
            </Typography>
          </Alert>
        </>
      )}

      {!canAccessMasterReset && (
        <Alert severity="info" sx={{ mt: 4, mb: 3 }}>
          <Typography variant="body2">
            <strong>Access Restricted:</strong> Master Reset options are only available to Tournament Administrators. 
            Contact your administrator if you need to perform system-wide operations.
          </Typography>
        </Alert>
      )}

      {canAccessMasterReset && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {resetOptions.map((option, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: option.type === 'master' ? '2px solid' : '1px solid',
                borderColor: option.type === 'master' ? 'error.main' : 'divider',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: `${option.color}.light`,
                      color: `${option.color}.contrastText`,
                      mr: 2
                    }}
                  >
                    {option.icon}
                  </Box>
                  <Typography variant="h6" component="h2">
                    {option.title}
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {option.description}
                </Typography>

                {option.count !== undefined && (
                  <Chip 
                    label={`${option.count} records`} 
                    size="small" 
                    color={option.count > 0 ? 'primary' : 'default'}
                    sx={{ mb: 1 }}
                  />
                )}

                {option.warning && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    <Typography variant="caption">
                      {option.warning}
                    </Typography>
                  </Alert>
                )}
              </CardContent>
              
              <CardActions>
                <Button
                  variant="contained"
                  color={option.color}
                  onClick={() => handleResetClick(option.type)}
                  disabled={loading || (option.count !== undefined && option.count === 0)}
                  sx={{ ml: 1 }}
                >
                  {option.count !== undefined && option.count === 0 ? 'No Data' : 'Reset'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
        </Grid>
      )}

      {/* Quick Info Section */}
      <Paper sx={{ p: 3, mt: 4, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          Quick Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" component="span">
                <strong>Current User:</strong> 
              </Typography>
              <Box component="span" sx={{ ml: 1 }}>
                {formatUserName(userProfile, { 
                  variant: 'body2',
                  color: 'text.secondary',
                  showIcon: true 
                })}
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Email:</strong> {userProfile?.email}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Organization:</strong> {userProfile?.organizationName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Role:</strong> {userProfile?.role}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Reset Confirmation Dialog */}
      <Dialog 
        open={resetDialog.open} 
        onClose={handleResetClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning color="error" />
            Confirm Reset Operation
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {resetDialog.type === 'master' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                MASTER RESET - This will delete EVERYTHING!
              </Typography>
              <Typography variant="body2">
                This operation will permanently delete:
              </Typography>
              <ul>
                <li>All Players ({collectionCounts.players || 0} records)</li>
                <li>All Tournaments ({collectionCounts.tournaments || 0} records)</li>
                <li>All Golf Courses ({collectionCounts.golfCourses || 0} records)</li>
                <li>All Matches ({collectionCounts.matches || 0} records)</li>
                <li>All Scores ({collectionCounts.scores || 0} records)</li>
                <li>All Restore Points ({collectionCounts.restorePoints || 0} records)</li>
              </ul>
              <Typography variant="body2" color="error" fontWeight="bold">
                Transaction Logs and Backups will be preserved.
              </Typography>
            </Alert>
          )}
          
          {resetDialog.type === 'players' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Player Reset
              </Typography>
              <Typography variant="body2">
                This will delete all {collectionCounts.players || 0} players and related data.
              </Typography>
            </Alert>
          )}
          
          {resetDialog.type === 'tournaments' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Tournament Reset
              </Typography>
              <Typography variant="body2">
                This will delete all {collectionCounts.tournaments || 0} tournaments and related data.
              </Typography>
            </Alert>
          )}

          {resetResult && (
            <Alert 
              severity={resetResult.success ? 'success' : 'error'} 
              sx={{ mt: 2 }}
            >
              {resetResult.success ? (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Reset Completed Successfully!
                  </Typography>
                  {resetResult.totalDeleted && (
                    <Typography variant="body2">
                      Total records deleted: {resetResult.totalDeleted}
                    </Typography>
                  )}
                  {resetResult.deletedCount && (
                    <Typography variant="body2">
                      Records deleted: {resetResult.deletedCount}
                    </Typography>
                  )}
                  {resetResult.message && (
                    <Typography variant="body2">
                      {resetResult.message}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2">
                  Error: {resetResult.error}
                </Typography>
              )}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Are you sure you want to proceed? This action cannot be undone.
          </Typography>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleResetClose} disabled={resetLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleResetConfirm} 
            color="error" 
            variant="contained"
            disabled={resetLoading}
            startIcon={resetLoading ? <CircularProgress size={20} /> : null}
          >
            {resetLoading ? 'Processing...' : 'Confirm Reset'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}